/**
 * Signup types and pure utility functions.
 * No React or component dependencies — components import from here, not the other way around.
 */
import {
  ApprovalStatus,
  BulkCreateSignupSingleShiftResponse,
  Event,
  EventStatus,
  Question,
  QuestionAnswer,
  Shift,
  ShiftType,
  UserIdentifier,
} from '@amzn/red-velvet-api';
import { isUnlistedEvent } from './eventUtils';

export enum SignupStatus {
  SIGNUP = 'Signed up',
  CANCEL = 'Signup Cancelled',
  PENDING = 'Pending',
}

export const WAIVER_ACCEPTED_KEY = 'waiverStatus.accepted';
export const WAIVER_DECLINED_KEY = 'waiverStatus.declined';

/** Result from a bulk signup operation, used to render post-signup alerts. */
export interface BulkSignupResult {
  succeededCount: number;
  noAccessUsers: UserIdentifier[];
}

/** Extracts a BulkSignupResult from a single-shift API response, or undefined if missing. */
export function toBulkSignupResult(
  shiftResponse: BulkCreateSignupSingleShiftResponse | undefined
): BulkSignupResult | undefined {
  if (!shiftResponse) return undefined;
  return {
    succeededCount: shiftResponse.succeededSignupUsers.length,
    noAccessUsers: shiftResponse.noGiveHubAccessSignupUsers,
  };
}

export interface AmazonSignup {
  signupId: string;
  shiftId: string;
  name: string;
  alias: string;
  email: string;
  building: string;
  managerLogin: string;
  numberOfGuests: number;
  tShirtSize: string;
  signupStatus: SignupStatus;
  waiverAccepted?: boolean;
  questionAnswers?: QuestionAnswer[];
}

/** Returns questions visible to the signup flow. VC-created questions (those with answerType) are hidden when the flag is off. */
export function getSignupVisibleQuestions(
  questions: Question[] | undefined,
  isEventCreationEnabled: boolean
): Question[] | undefined {
  if (!questions || isEventCreationEnabled) return questions;
  const filtered = questions.filter((q) => !q.answerType);
  return filtered.length > 0 ? filtered : undefined;
}

export function isShiftManageSignupBlocked(event?: Event, shift?: Shift): boolean {
  return (
    (event !== undefined && isUnlistedEvent(event)) ||
    shift?.shiftType === ShiftType.WAITLIST ||
    shift?.shiftType === ShiftType.DROP_IN
  );
}

export enum SignupBlockedReason {
  SIGNUPS_CLOSED,
  SHIFT_FULL,
  REQUIRED_WAIVER,
  CUSTOM_QUESTIONS,
  TSHIRT_REQUIRED,
  UNSUPPORTED_SHIFT_TYPE,
  UNPUBLISHED_EVENT,
  ARCHIVED_EVENT,
  UN_APPROVAL_EVENT,
  DRAFT_EVENT, // Covers both DRAFT and UNPUBLISHED statuses
}

/**
 * Computes effective signup capacity info considering inline status edits and guest counts.
 * - activeSignupCount: signups that are not cancelled (after inline edits)
 * - totalOccupiedSpots: active signups + their guests
 * - remainingSpots: spots still available (Infinity if unlimited)
 */
export function computeSignupCapacity(
  itemsWithChanges: AmazonSignup[],
  shift: { maxSignup: number }
) {
  const activeSignups = itemsWithChanges.filter((s) => s.signupStatus !== SignupStatus.CANCEL);
  const activeSignupCount = activeSignups.length;
  const totalOccupiedSpots = activeSignups.reduce((sum, s) => sum + 1 + (s.numberOfGuests ?? 0), 0);

  const isUnlimited = shift.maxSignup === 0;
  const remainingSpots = isUnlimited ? Infinity : shift.maxSignup - totalOccupiedSpots;

  return { activeSignupCount, totalOccupiedSpots, remainingSpots, isUnlimited };
}

export function getSignupBlockedReasons(
  event: Event,
  shift: Shift,
  remainingSpots?: number
): SignupBlockedReason[] {
  const reasons: SignupBlockedReason[] = [];
  if (isShiftManageSignupBlocked(event, shift))
    reasons.push(SignupBlockedReason.UNSUPPORTED_SHIFT_TYPE);
  if (!shift.signupsOpen) reasons.push(SignupBlockedReason.SIGNUPS_CLOSED);
  if (shift.maxSignup > 0 && remainingSpots !== undefined && remainingSpots <= 0)
    reasons.push(SignupBlockedReason.SHIFT_FULL);
  if (event.waiver && event.waiver.acceptance === 'required')
    reasons.push(SignupBlockedReason.REQUIRED_WAIVER);
  if (event.questions && event.questions.length > 0)
    reasons.push(SignupBlockedReason.CUSTOM_QUESTIONS);
  if (event.hasTShirt) reasons.push(SignupBlockedReason.TSHIRT_REQUIRED);
  if (
    event.status !== undefined &&
    event.status !== EventStatus.PUBLISHED &&
    event.status !== EventStatus.DRAFT
  )
    reasons.push(SignupBlockedReason.UNPUBLISHED_EVENT);
  if (event.archived === true) reasons.push(SignupBlockedReason.ARCHIVED_EVENT);
  if (event.approvalStatus !== ApprovalStatus.APPROVED)
    reasons.push(SignupBlockedReason.UN_APPROVAL_EVENT);
  if (event.status === EventStatus.DRAFT || event.status === EventStatus.UNPUBLISHED)
    reasons.push(SignupBlockedReason.DRAFT_EVENT);
  return reasons;
}

const GIVEHUB_RESOLVABLE_REASONS = new Set([
  SignupBlockedReason.REQUIRED_WAIVER,
  SignupBlockedReason.CUSTOM_QUESTIONS,
  SignupBlockedReason.TSHIRT_REQUIRED,
]);

export function shouldShowSignupLink(blockedReasons: SignupBlockedReason[]): boolean {
  return (
    blockedReasons.length > 0 && blockedReasons.every((r) => GIVEHUB_RESOLVABLE_REASONS.has(r))
  );
}

const EVENT_LEVEL_BLOCKED_REASONS = new Set([
  SignupBlockedReason.UNPUBLISHED_EVENT,
  SignupBlockedReason.ARCHIVED_EVENT,
  SignupBlockedReason.UN_APPROVAL_EVENT,
  SignupBlockedReason.UNSUPPORTED_SHIFT_TYPE,
  SignupBlockedReason.DRAFT_EVENT,
]);

export function hasEventLevelBlock(reasons: SignupBlockedReason[]): boolean {
  return reasons.some((r) => EVENT_LEVEL_BLOCKED_REASONS.has(r));
}

export const SIGNUP_CSV_HEADER_KEYS = [
  'csvHeaders.shiftName',
  'csvHeaders.shiftDate',
  'csvHeaders.shiftTime',
  'csvHeaders.name',
  'csvHeaders.alias',
  'csvHeaders.email',
  'csvHeaders.building',
  'csvHeaders.managerAlias',
  'csvHeaders.numberOfGuests',
  'csvHeaders.tShirtSize',
  'csvHeaders.signupStatus',
] as const;

export type TFunction = (key: string) => string;

export function getSignupCsvHeaders(t: TFunction): string[] {
  return SIGNUP_CSV_HEADER_KEYS.map((key) => t(key));
}

export function formatDateForCsv(date: Date | undefined, timezone: string, locale: string): string {
  if (!date) return '';
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  });
}

export function formatTimeForCsv(date: Date | undefined, timezone: string, locale: string): string {
  if (!date) return '';
  return date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: timezone,
  });
}

export function formatDateTimeForCsv(
  date: Date | undefined,
  timezone: string,
  locale: string
): string {
  if (!date) return '';
  return date.toLocaleString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: timezone,
  });
}

/**
 * Builds CSV headers dynamically based on event configuration.
 * Base columns are always included. Waiver and question columns are conditional.
 */
export function buildSignupCsvHeaders(event: Event, t: TFunction): string[] {
  const headers = getSignupCsvHeaders(t);

  if (event.waiver && event.waiver.acceptance !== 'none') {
    headers.push(t('csvHeaders.waiver'));
  }

  if (event.questions) {
    for (const question of event.questions) {
      headers.push(question.title || '');
    }
  }

  return headers;
}

/**
 * Resolves the Email cell for the signup CSV. Prefers the enriched primaryEmail
 * (`signup.email`); when that is blank (e.g. PAPI returned no primary email for the
 * employee), falls back to `<alias>@amazon.com`. This is safe because @amazon.com and
 * regional domains (.co.uk, .co.jp, etc.) are proxy aliases to the same mailbox, so
 * `<alias>@amazon.com` reaches the person even when their primary email is on a regional
 * domain. Returns '' only when both the email and alias are empty.
 * Scoped to the CSV download only — does not mutate AmazonSignup.email elsewhere.
 */
export function getSignupCsvEmail(signup: AmazonSignup): string {
  if (signup.email) return signup.email;
  if (signup.alias) return `${signup.alias}@amazon.com`;
  return '';
}

export function buildSignupCsvRows(
  signups: AmazonSignup[],
  shift: Shift,
  event: Event,
  contentTimezone: string,
  t: TFunction,
  locale: string
): string[][] {
  const shiftName = shift.name || '';
  const startDate = shift.startDateTime || event.startTimestamp;

  const hasWaiver = !!(event.waiver && event.waiver.acceptance !== 'none');
  const questions = event.questions;

  return signups.map((signup) => {
    const baseRow = [
      shiftName,
      formatDateForCsv(startDate, contentTimezone, locale),
      formatTimeForCsv(startDate, contentTimezone, locale),
      signup.name,
      signup.alias,
      getSignupCsvEmail(signup),
      signup.building,
      signup.managerLogin,
      signup.numberOfGuests ? String(signup.numberOfGuests) : '',
      signup.tShirtSize,
      signup.signupStatus,
    ];

    if (hasWaiver) {
      baseRow.push(
        signup.waiverAccepted === true
          ? t(WAIVER_ACCEPTED_KEY)
          : signup.waiverAccepted === false
            ? t(WAIVER_DECLINED_KEY)
            : ''
      );
    }

    if (questions && questions.length > 0) {
      const resolved = resolveQuestionAnswers(signup.questionAnswers, questions);
      for (const question of questions) {
        baseRow.push(resolved.get(question.id ?? '') || '');
      }
    }

    return baseRow;
  });
}

export function formatDateTimeForFilename(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value || '';

  return `${get('year')}-${get('month')}-${get('day')}-${get('hour')}${get('minute')}`;
}

export function buildSignupCsvFilename(
  eventId: string,
  shiftName: string,
  filenameTimezone: string
): string {
  // Remove non-ASCII and filesystem-unsafe characters (e.g. Japanese chars, #, /, :)
  const sanitizedShiftName = (shiftName || '').replace(/[^\w\s-]/g, '').trim();
  const timestamp = formatDateTimeForFilename(new Date(), filenameTimezone);
  return `Signups-${eventId}-${sanitizedShiftName}-${timestamp}.csv`;
}

/**
 * Resolves a signup's QuestionAnswer records to display text using the Event's Question definitions.
 * Returns a Map<questionId, displayText>.
 */
export function resolveQuestionAnswers(
  questionAnswers: QuestionAnswer[] | undefined,
  questions: Question[] | undefined
): Map<string, string> {
  const result = new Map<string, string>();

  if (!questions) return result;

  for (const question of questions) {
    const qId = question.id;
    if (!qId) continue;

    if (!questionAnswers) {
      result.set(qId, '');
      continue;
    }

    const matching = questionAnswers.filter((qa) => qa.questionId === qId);

    if (matching.length === 0) {
      result.set(qId, '');
      continue;
    }

    switch (question.answerType) {
      case 'TEXT_SINGLE': {
        result.set(qId, matching[0].textValue || '');
        break;
      }
      case 'CHOICES_SINGLE': {
        const optionId = matching[0].optionId;
        const label = question.options?.find((o) => o.id === optionId)?.label;
        result.set(qId, label || '');
        break;
      }
      case 'CHOICES_MULTIPLE': {
        const labels = matching
          .map((qa) => question.options?.find((o) => o.id === qa.optionId)?.label)
          .filter((label): label is string => !!label);
        result.set(qId, labels.join(', '));
        break;
      }
      default:
        result.set(qId, '');
    }
  }

  return result;
}
